// PipelineCompareReport.tsx
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  Button,
  Table,
  Spinner,
  FormGroup,
} from "reactstrap";
import axios from "axios";
import { statusOrder as baseStatusOrder } from "./PipeLineReport";

type ID = number;

interface PipeLineReportData {
  id: ID;
  title: string;
  status: string;
  projected_revenue: number;
  dateAdded: string;
  ownerId: number;
  name: string; // owner full name
  companyName: string;
  notes: string;
  notesDate: string;
}

interface PipelineReportSummary {
  status: string;
  jobCount: number;
  totalRevenue: number;
}

type SortDir = "asc" | "desc";
type ChangeType =
  | "All"
  | "New"
  | "Removed"
  | "Status Change"
  | "Revenue Change"
  | "Unchanged";

interface DiffRow {
  id: ID;
  title: string;
  companyName: string;
  ownerId: number | null;
  ownerName: string | null;

  beforeStatus: string | null;
  afterStatus: string | null;

  beforeRevenue: number | null;
  afterRevenue: number | null;

  beforeNotesDate?: string | null;
  afterNotesDate?: string | null;

  deltaRevenue: number | null; // after - before
  changeType: Exclude<ChangeType, "All">;
}

// --- Date helpers (LOCAL-safe; no UTC shifts) ---
const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const todayLocalYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const daysAgoLocalYMD = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const labelFromYMD = (ymd: string) => {
  // "YYYY-MM-DD" -> locale label without Date() (avoids TZ shifts)
  const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
  return `${m}/${d}/${y}`;
};

const PipelineCompareReport: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(daysAgoLocalYMD(7));
  const [endDate, setEndDate] = useState<string>(todayLocalYMD());

  const [startData, setStartData] = useState<PipeLineReportData[]>([]);
  const [endData, setEndData] = useState<PipeLineReportData[]>([]);
  const [owners, setOwners] = useState<{ id: number | "All"; name: string }[]>(
    []
  );

  const [selectedOwner, setSelectedOwner] = useState<number | "All">("All");

  // NEW: multi-select statuses (no Placed/Lost here – baseStatusOrder should already be "active" only)
  const [selectedStatuses, setSelectedStatuses] =
    useState<string[]>(baseStatusOrder);

  const [selectedChangeType, setSelectedChangeType] =
    useState<ChangeType>("All");

  const [diffRows, setDiffRows] = useState<DiffRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<keyof DiffRow | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const abortCtrlRef = useRef<AbortController | null>(null);

  const groupSummary = (arr: PipeLineReportData[]) => {
    return arr.reduce((acc, item) => {
      if (!acc[item.status]) {
        acc[item.status] = {
          status: item.status,
          jobCount: 0,
          totalRevenue: 0,
        };
      }
      acc[item.status].jobCount += 1;
      acc[item.status].totalRevenue += item.projected_revenue || 0;
      return acc;
    }, {} as Record<string, PipelineReportSummary>);
  };

  const sumActivePipeline = (
    summary: Record<string, PipelineReportSummary>
  ) => {
    // Active = only statuses in baseStatusOrder (no Placed/Lost)
    return baseStatusOrder.reduce(
      (acc, s) => acc + (summary[s]?.totalRevenue || 0),
      0
    );
  };

  const totalValue = (summary: Record<string, PipelineReportSummary>) =>
    Object.values(summary).reduce((acc, x) => acc + (x.totalRevenue || 0), 0);

  const fetchSnapshots = async () => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    abortCtrlRef.current = controller;

    try {
      const [resStart, resEnd] = await Promise.all([
        axios.get<PipeLineReportData[]>("/pipeline-delta", {
          params: { asOfDate: startDate }, // pass exact YMD
          signal: controller.signal,
          timeout: 10000,
        }),
        axios.get<PipeLineReportData[]>("/pipeline-delta", {
          params: { asOfDate: endDate },
          signal: controller.signal,
          timeout: 10000,
        }),
      ]);

      const start = [...resStart.data];
      const end = [...resEnd.data];

      // Owners
      const ownerMap = new Map<number, string>();
      end.forEach((x) => ownerMap.set(x.ownerId, x.name));
      start.forEach((x) => ownerMap.set(x.ownerId, x.name));
      setOwners([
        { id: "All", name: "Sales Team" },
        ...Array.from(ownerMap).map(([id, name]) => ({ id, name })),
      ]);

      // Sort by status order then value
      const statusIndex = (s: string) => {
        const i = baseStatusOrder.indexOf(s);
        return i === -1 ? 999 : i;
      };
      const byStatusThenValue = (
        a: PipeLineReportData,
        b: PipeLineReportData
      ) => {
        const ai = statusIndex(a.status);
        const bi = statusIndex(b.status);
        if (ai !== bi) return ai - bi;
        return (b.projected_revenue || 0) - (a.projected_revenue || 0);
      };

      start.sort(byStatusThenValue);
      end.sort(byStatusThenValue);

      setStartData(start);
      setEndData(end);

      // Build diff rows
      const startById = new Map<ID, PipeLineReportData>(
        start.map((x) => [x.id, x])
      );
      const endById = new Map<ID, PipeLineReportData>(
        end.map((x) => [x.id, x])
      );
      const allIds = new Set<ID>([...startById.keys(), ...endById.keys()]);

      const diffs: DiffRow[] = [];
      for (const id of allIds) {
        const a = startById.get(id) || null;
        const b = endById.get(id) || null;

        if (a && !b) {
          diffs.push({
            id,
            title: a.title,
            companyName: a.companyName,
            ownerId: a.ownerId ?? null,
            ownerName: a.name ?? null,
            beforeStatus: a.status,
            afterStatus: null,
            beforeRevenue: a.projected_revenue ?? 0,
            afterRevenue: null,
            beforeNotesDate: a.notesDate || null,
            afterNotesDate: null,
            deltaRevenue:
              a.projected_revenue != null ? -a.projected_revenue : null,
            changeType: "Removed",
          });
          continue;
        }
        if (!a && b) {
          diffs.push({
            id,
            title: b.title,
            companyName: b.companyName,
            ownerId: b.ownerId ?? null,
            ownerName: b.name ?? null,
            beforeStatus: null,
            afterStatus: b.status,
            beforeRevenue: null,
            afterRevenue: b.projected_revenue ?? 0,
            beforeNotesDate: null,
            afterNotesDate: b.notesDate || null,
            deltaRevenue: b.projected_revenue ?? null,
            changeType: "New",
          });
          continue;
        }
        if (a && b) {
          const statusChanged = a.status !== b.status;
          const before = a.projected_revenue ?? 0;
          const after = b.projected_revenue ?? 0;
          const revDelta = after - before;
          const revenueChanged = Math.abs(revDelta) > 0;

          let changeType: DiffRow["changeType"] = "Unchanged";
          if (statusChanged) changeType = "Status Change";
          else if (revenueChanged) changeType = "Revenue Change";

          diffs.push({
            id,
            title: b.title || a.title,
            companyName: b.companyName || a.companyName,
            ownerId: b.ownerId ?? a.ownerId ?? null,
            ownerName: b.name ?? a.name ?? null,
            beforeStatus: a.status,
            afterStatus: b.status,
            beforeRevenue: before,
            afterRevenue: after,
            beforeNotesDate: a.notesDate || null,
            afterNotesDate: b.notesDate || null,
            deltaRevenue: revDelta,
            changeType,
          });
        }
      }

      const changeRank = (t: DiffRow["changeType"]) =>
        t === "Status Change"
          ? 0
          : t === "Revenue Change"
          ? 1
          : t === "New"
          ? 2
          : t === "Removed"
          ? 3
          : 4;

      diffs.sort((x, y) => {
        const cr = changeRank(x.changeType) - changeRank(y.changeType);
        if (cr !== 0) return cr;
        const ax = x.afterRevenue ?? x.beforeRevenue ?? 0;
        const ay = y.afterRevenue ?? y.beforeRevenue ?? 0;
        return ay - ax;
      });

      setDiffRows(diffs);
      setLoading(false);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to load pipeline snapshots");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSnapshots();
    return () => abortCtrlRef.current?.abort();
  }, []);

  const ownerOptions = useMemo(() => owners, [owners]);

  // --- Filtered snapshot data for summaries / Active Pipeline Δ ---
  const filteredStartData = useMemo(() => {
    return startData.filter((item) => {
      const ownerOk =
        selectedOwner === "All" ? true : item.ownerId === selectedOwner;
      const statusOk =
        !selectedStatuses.length ||
        selectedStatuses.includes(item.status || "");
      return ownerOk && statusOk;
    });
  }, [startData, selectedOwner, selectedStatuses]);

  const filteredEndData = useMemo(() => {
    return endData.filter((item) => {
      const ownerOk =
        selectedOwner === "All" ? true : item.ownerId === selectedOwner;
      const statusOk =
        !selectedStatuses.length ||
        selectedStatuses.includes(item.status || "");
      return ownerOk && statusOk;
    });
  }, [endData, selectedOwner, selectedStatuses]);

  const startSummary = useMemo(
    () => groupSummary(filteredStartData),
    [filteredStartData]
  );
  const endSummary = useMemo(
    () => groupSummary(filteredEndData),
    [filteredEndData]
  );

  // Filtering DIFF rows
  const filteredRows = useMemo(() => {
    return diffRows.filter((r) => {
      const ownerOk =
        selectedOwner === "All" ? true : r.ownerId === selectedOwner;

      const statusOk =
        !selectedStatuses.length ||
        selectedStatuses.some(
          (s) => r.beforeStatus === s || r.afterStatus === s
        );

      const changeOk =
        selectedChangeType === "All"
          ? true
          : r.changeType === selectedChangeType;

      return ownerOk && statusOk && changeOk;
    });
  }, [diffRows, selectedOwner, selectedStatuses, selectedChangeType]);

  // Sorting DIFF rows
  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;

    const val = (r: DiffRow, k: keyof DiffRow) => {
      const v = r[k] as any;
      if (
        k === "deltaRevenue" ||
        k === "beforeRevenue" ||
        k === "afterRevenue"
      ) {
        return v ?? 0;
      }
      if (
        k === "beforeStatus" ||
        k === "afterStatus" ||
        k === "changeType" ||
        k === "title" ||
        k === "companyName" ||
        k === "ownerName"
      ) {
        return (v || "") as string;
      }
      return v ?? "";
    };

    const out = [...filteredRows].sort((a, b) => {
      const av = val(a, sortKey);
      const bv = val(b, sortKey);

      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }

      const comp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? comp : -comp;
    });

    return out;
  }, [filteredRows, sortKey, sortDir]);

  const requestSort = (k: keyof DiffRow) => {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  };

  const handleDownloadCSV = () => {
    if (!diffRows.length) return;

    const headers = [
      "ID",
      "Title",
      "Company",
      "Owner",
      "Change Type",
      "Before Status",
      "After Status",
      "Before Value",
      "After Value",
      "Delta Value",
      "Before Note Date",
      "After Note Date",
    ];

    const rows = diffRows.map((r) => [
      r.id,
      r.title ?? "",
      r.companyName ?? "",
      r.ownerName ?? "",
      r.changeType,
      r.beforeStatus ?? "",
      r.afterStatus ?? "",
      r.beforeRevenue != null ? r.beforeRevenue.toFixed(2) : "",
      r.afterRevenue != null ? r.afterRevenue.toFixed(2) : "",
      r.deltaRevenue != null ? r.deltaRevenue.toFixed(2) : "",
      r.beforeNotesDate || "",
      r.afterNotesDate || "",
    ]);

    const csv = [headers, ...rows]
      .map((row) =>
        row.map((f) => `"${String(f).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute(
      "download",
      `pipeline_compare_${startDate}_vs_${endDate}.csv`
    );
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // --- Totals for the bottom table (NOW RESPECT FILTERS) ---
  const totals = useMemo(() => {
    let before = 0;
    let after = 0;
    filteredRows.forEach((r) => {
      before += r.beforeRevenue ?? 0;
      after += r.afterRevenue ?? 0;
    });
    const delta = after - before;
    return { before, after, delta };
  }, [filteredRows]);

  // --- Active pipeline delta (now respects Owner + Status filters) ---
  const activeDelta = useMemo(() => {
    const s = sumActivePipeline(startSummary);
    const e = sumActivePipeline(endSummary);
    return { start: s, end: e, delta: e - s };
  }, [startSummary, endSummary]);

  // NEW: Pipeline New summary (filtered)
  const pipelineNew = useMemo(() => {
    const newRows = filteredRows.filter((r) => r.changeType === "New");
    const count = newRows.length;
    const total = newRows.reduce((sum, r) => sum + (r.afterRevenue ?? 0), 0);
    return { count, total };
  }, [filteredRows]);

  const handleStatusMultiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectEl = e.target as unknown as HTMLSelectElement;
    const values = Array.from(selectEl.selectedOptions).map((o) => o.value);
    setSelectedStatuses(values);
  };

  if (loading) {
    return (
      <>
        <div>Comparing snapshots…</div>
        <Spinner color='primary' />
      </>
    );
  }
  if (error) {
    return <p>Error: {error}</p>;
  }

  return (
    <Card>
      <CardHeader>
        <Row className='g-2 align-items-center'>
          <Col xs='12' md='auto'>
            <Label className='mb-0'>Start</Label>
            <Input
              type='date'
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ maxWidth: 200 }}
            />
          </Col>
          <Col xs='12' md='auto'>
            <Label className='mb-0'>End</Label>
            <Input
              type='date'
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ maxWidth: 200 }}
            />
          </Col>
          <Col xs='12' md='auto'>
            <Button
              color='primary'
              onClick={fetchSnapshots}
              disabled={!startDate || !endDate}
            >
              Refresh
            </Button>
          </Col>
          <Col xs='12' md='auto'>
            <Button
              color='secondary'
              onClick={handleDownloadCSV}
              disabled={!diffRows.length}
            >
              Download Diff CSV
            </Button>
          </Col>

          <Col xs='12' md className='ms-md-auto'>
            <Row className='g-2'>
              <Col xs='12' md='4'>
                <Label className='mb-0'>Owner</Label>
                <Input
                  type='select'
                  value={selectedOwner}
                  onChange={(e) =>
                    setSelectedOwner(
                      e.target.value === "All"
                        ? "All"
                        : parseInt(e.target.value)
                    )
                  }
                >
                  {ownerOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs='12' md='4'>
                <Label className='mb-0'>Status (multi-select)</Label>
                <Input
                  type='select'
                  multiple
                  value={selectedStatuses}
                  onChange={handleStatusMultiChange}
                >
                  {baseStatusOrder.map((s) => (
                    <option value={s} key={s}>
                      {s}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs='12' md='4'>
                <Label className='mb-0'>Change Type</Label>
                <Input
                  type='select'
                  value={selectedChangeType}
                  onChange={(e) =>
                    setSelectedChangeType(e.target.value as ChangeType)
                  }
                >
                  {[
                    "All",
                    "New",
                    "Removed",
                    "Status Change",
                    "Revenue Change",
                    "Unchanged",
                  ].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Quick KPIs */}
        <Row className='g-2 mt-3'>
          <Col md='auto'>
            <div
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: 6,
                display: "inline-block",
                background: "#f9fafb",
                whiteSpace: "nowrap",
              }}
            >
              <strong>
                Active Pipeline Δ $ ({labelFromYMD(startDate)} →{" "}
                {labelFromYMD(endDate)}):{" "}
              </strong>
              <span>
                {activeDelta.delta >= 0 ? "+" : "-"}$
                {Math.abs(activeDelta.delta).toLocaleString()}
              </span>
              <span className='ms-2' style={{ color: "#666" }}>
                (Start: ${activeDelta.start.toLocaleString()} • End: $
                {activeDelta.end.toLocaleString()})
              </span>
            </div>
          </Col>

          {/* NEW: Pipeline New summary */}
          <Col md='auto'>
            <div
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: 6,
                display: "inline-block",
                background: "#f0fdf4",
                whiteSpace: "nowrap",
              }}
            >
              <strong>
                Pipeline New $ ({labelFromYMD(startDate)} →{" "}
                {labelFromYMD(endDate)}):{" "}
              </strong>
              <span>${pipelineNew.total.toLocaleString()}</span>
              <span className='ms-2' style={{ color: "#166534" }}>
                ({pipelineNew.count} new opp
                {pipelineNew.count === 1 ? "" : "s"})
              </span>
            </div>
          </Col>
        </Row>
      </CardHeader>

      {/* SINGLE combined summary table (now filtered, no Placed/Lost) */}
      <CardHeader>
        <strong>
          Pipeline Summary by Phase ({labelFromYMD(startDate)} vs{" "}
          {labelFromYMD(endDate)})
        </strong>
        <Table bordered striped size='sm' className='mt-2'>
          <thead>
            <tr>
              <th>Phase</th>
              <th>Count ({labelFromYMD(startDate)})</th>
              <th>$ ({labelFromYMD(startDate)})</th>
              <th>Count ({labelFromYMD(endDate)})</th>
              <th>$ ({labelFromYMD(endDate)})</th>
            </tr>
          </thead>
          <tbody>
            {baseStatusOrder.map((s) => {
              const a = startSummary[s];
              const b = endSummary[s];
              return (
                <tr key={s}>
                  <td>{s}</td>
                  <td>{a?.jobCount?.toLocaleString?.() ?? 0}</td>
                  <td>
                    {a?.totalRevenue
                      ? `$${a.totalRevenue.toLocaleString()}`
                      : "$0"}
                  </td>
                  <td>{b?.jobCount?.toLocaleString?.() ?? 0}</td>
                  <td>
                    {b?.totalRevenue
                      ? `$${b.totalRevenue.toLocaleString()}`
                      : "$0"}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td>
                <strong>Total (All Phases)</strong>
              </td>
              <td colSpan={2}>
                <strong>${totalValue(startSummary).toLocaleString()}</strong>
              </td>
              <td colSpan={2}>
                <strong>${totalValue(endSummary).toLocaleString()}</strong>
              </td>
            </tr>
          </tbody>
        </Table>
      </CardHeader>

      {/* DIFF TABLE */}
      <CardBody>
        <h5 className='mt-2'>
          Opportunity Changes ({labelFromYMD(startDate)} →{" "}
          {labelFromYMD(endDate)})
        </h5>
        <Table bordered striped responsive>
          <thead>
            <tr>
              <th
                onClick={() => requestSort("changeType")}
                style={{ cursor: "pointer" }}
              >
                Change
              </th>
              <th
                onClick={() => requestSort("id")}
                style={{ cursor: "pointer" }}
              >
                ID
              </th>
              <th
                onClick={() => requestSort("title")}
                style={{ cursor: "pointer" }}
              >
                Title
              </th>
              <th
                onClick={() => requestSort("companyName")}
                style={{ cursor: "pointer" }}
              >
                Company
              </th>
              <th
                onClick={() => requestSort("ownerName")}
                style={{ cursor: "pointer" }}
              >
                Owner
              </th>
              <th
                onClick={() => requestSort("beforeStatus")}
                style={{ cursor: "pointer" }}
              >
                Before Status
              </th>
              <th
                onClick={() => requestSort("afterStatus")}
                style={{ cursor: "pointer" }}
              >
                After Status
              </th>
              <th
                onClick={() => requestSort("beforeRevenue")}
                style={{ cursor: "pointer" }}
              >
                Before $
              </th>
              <th
                onClick={() => requestSort("afterRevenue")}
                style={{ cursor: "pointer" }}
              >
                After $
              </th>
              <th
                onClick={() => requestSort("deltaRevenue")}
                style={{ cursor: "pointer" }}
              >
                Δ $
              </th>
              <th>Links</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length ? (
              <>
                {sortedRows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.changeType}</td>
                    <td>
                      <a
                        href={`https://cls44.bullhornstaffing.com/BullhornSTAFFING/OpenWindow.cfm?Entity=JobOrder&id=${r.id}`}
                        target='_blank'
                        rel='noreferrer'
                      >
                        {r.id}
                      </a>
                    </td>
                    <td>{r.title}</td>
                    <td>{r.companyName}</td>
                    <td>{r.ownerName}</td>
                    <td>{r.beforeStatus ?? ""}</td>
                    <td>{r.afterStatus ?? ""}</td>
                    <td>
                      {r.beforeRevenue != null
                        ? `$${r.beforeRevenue.toLocaleString()}`
                        : ""}
                    </td>
                    <td>
                      {r.afterRevenue != null
                        ? `$${r.afterRevenue.toLocaleString()}`
                        : ""}
                    </td>
                    <td>
                      {r.deltaRevenue != null
                        ? (r.deltaRevenue >= 0 ? "+" : "-") +
                          `$${Math.abs(r.deltaRevenue).toLocaleString()}`
                        : ""}
                    </td>
                    <td>
                      {r.afterStatus && (
                        <a
                          href={`https://cls44.bullhornstaffing.com/BullhornSTAFFING/OpenWindow.cfm?Entity=JobOrder&id=${r.id}`}
                          target='_blank'
                          rel='noreferrer'
                        >
                          View
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
                {/* Totals row – now based on filteredRows */}
                <tr>
                  <td colSpan={7} className='text-end'>
                    <strong>Totals:</strong>
                  </td>
                  <td>
                    <strong>${totals.before.toLocaleString()}</strong>
                  </td>
                  <td>
                    <strong>${totals.after.toLocaleString()}</strong>
                  </td>
                  <td>
                    <strong>
                      {(totals.delta >= 0 ? "+" : "-") +
                        `$${Math.abs(totals.delta).toLocaleString()}`}
                    </strong>
                  </td>
                  <td />
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={11} className='text-center'>
                  No changes found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </CardBody>

      <CardBody>
        <FormGroup>
          <Label className='fw-bold mb-2'>Tips</Label>
          <ul className='mb-0'>
            <li>
              Use Owner / Status / Change Type filters to isolate what you need.
            </li>
            <li>Click headers (e.g., Δ $) to sort by biggest movers.</li>
            <li>
              “Active Pipeline Δ $” excludes Placed/Lost and now reflects the
              selected Owner / Status filters.
            </li>
            <li>
              “Pipeline New” shows total value and count of newly added
              opportunities under the current filters.
            </li>
          </ul>
        </FormGroup>
      </CardBody>
    </Card>
  );
};

export default PipelineCompareReport;
